import type { CurrentUser } from '@zhiyue/core';
import { createAppRequest } from './request';

const OPEN_PLATFORM_TOKEN_STORAGE_KEY = 'finance.openPlatformAccessToken';
const TOKEN_REFRESH_BUFFER_MS = 60_000;
const request = createAppRequest();

export type OpenPlatformTokenResult = {
  accessToken: string;
  expiresAt: number;
};

type OpenPlatformTokenContext = Pick<CurrentUser, 'memberUuid'> & {
  organizationUuid?: string;
};

type CachedOpenPlatformToken = OpenPlatformTokenResult & {
  cacheKey: string;
};

let pendingRequest:
  | { cacheKey: string; promise: Promise<OpenPlatformTokenResult> }
  | undefined;

const normalizeText = (value: unknown) => String(value ?? '').trim();

const getCacheKey = (currentUser: OpenPlatformTokenContext) =>
  [
    normalizeText(currentUser.organizationUuid),
    normalizeText(currentUser.memberUuid),
  ].join('|');

const readCachedToken = (cacheKey: string): OpenPlatformTokenResult | null => {
  try {
    const rawValue = sessionStorage.getItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
    if (!rawValue) return null;

    const value = JSON.parse(rawValue) as CachedOpenPlatformToken;
    if (
      value.cacheKey !== cacheKey ||
      typeof value.accessToken !== 'string' ||
      !value.accessToken.trim() ||
      typeof value.expiresAt !== 'number' ||
      value.expiresAt <= Date.now() + TOKEN_REFRESH_BUFFER_MS
    ) {
      sessionStorage.removeItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
      return null;
    }

    return { accessToken: value.accessToken, expiresAt: value.expiresAt };
  } catch {
    sessionStorage.removeItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
    return null;
  }
};

export const clearOpenPlatformToken = () => {
  pendingRequest = undefined;
  sessionStorage.removeItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
};

export const getCachedOpenPlatformToken = (
  currentUser: OpenPlatformTokenContext,
): OpenPlatformTokenResult | null => readCachedToken(getCacheKey(currentUser));

export const fetchOpenPlatformToken = async (
  currentUser: OpenPlatformTokenContext,
): Promise<OpenPlatformTokenResult> => {
  const cacheKey = getCacheKey(currentUser);
  if (
    !normalizeText(currentUser.organizationUuid) ||
    !normalizeText(currentUser.memberUuid)
  ) {
    clearOpenPlatformToken();
    throw new Error('当前会话缺少开放平台认证信息');
  }

  const cachedToken = readCachedToken(cacheKey);
  if (cachedToken) return cachedToken;
  if (pendingRequest?.cacheKey === cacheKey) return pendingRequest.promise;

  const promise = request<OpenPlatformTokenResult>('/bff/open-platform/token', {
    method: 'GET',
    skipErrorHandler: true,
  }).then((result) => {
    if (
      typeof result?.accessToken !== 'string' ||
      !result.accessToken.trim() ||
      typeof result.expiresAt !== 'number'
    ) {
      throw new Error('开放平台 Token 响应无效');
    }
    sessionStorage.setItem(
      OPEN_PLATFORM_TOKEN_STORAGE_KEY,
      JSON.stringify({ ...result, cacheKey }),
    );
    return result;
  });

  pendingRequest = { cacheKey, promise };
  try {
    return await promise;
  } finally {
    if (pendingRequest?.promise === promise) pendingRequest = undefined;
  }
};
